<template>
  <div class="w-100">
    <modal-portal to="top" v-if="!message" class="mb-3">
      <template v-if="!message && !sendable">
        <message-compose-direct :reference="reference" v-model="direct"/>
        <!-- CC lives on the parent, not inside message-compose-direct: `direct`
             is spread last into the payload, so a cc key there would clobber a
             reply's CC list with an empty array -->
        <modal-row label="CC" v-if="canCc">
          <b-form-tags v-model="cc" size="sm" separator=" ,;" remove-on-delete
                       placeholder="Add a CC address…" :tag-validator="validEmail"
                       invalid-tag-text="Not a valid email address"
                       duplicate-tag-text="Already added"/>
        </modal-row>
        <modal-row label="" v-if="canAssignReplies">
          <!-- available when composing too, not just when replying: a first
               message to a contact is exactly when you want their answer -->
          <b-form-checkbox v-model="assign_replies_to_me" switch class="small"
                           v-b-tooltip.hover
                           title="Any reply to this message is assigned to you automatically instead of landing in the unassigned queue">
            Assign replies to me
          </b-form-checkbox>
        </modal-row>
      </template>
      <template v-else>
        <modal-row label="Reference">
          <b-input size="sm" :value="sendable.item_ref" :readonly="true" class="bg-white"/>
        </modal-row>
        <modal-row label="Send Via">
          <b-input size="sm" :value="sendable.source == 't' ? 'eBay' : 'Email'" :readonly="true"
                   class="bg-white"/>
        </modal-row>
        <modal-row label="To">
          <b-input size="sm" :value="sendable.account_address" :readonly="true" class="bg-white"/>
        </modal-row>
        <modal-row label="CC" v-if="canCc">
          <b-form-tags v-model="cc" size="sm" separator=" ,;" remove-on-delete
                       placeholder="Add a CC address…" :tag-validator="validEmail"
                       invalid-tag-text="Not a valid email address"/>
        </modal-row>
        <modal-row label="Subject">
          <b-input size="sm" v-model="direct.subject"/>
        </modal-row>
      </template>
    </modal-portal>

    <b-alert v-if="dncWarning" show variant="warning" class="py-2 px-3 mb-2 small">
      <strong>Do Not Contact.</strong> {{ dncWarning }}
    </b-alert>

    <b-row :no-gutters="$mediaBreakpointDown('lg') || !message" class="w-100">
      <b-col cols="12" :lg="message ? 8 : 12" class="text-left">
        <b-card no-body>
          <b-tabs card v-model="tab">
            <!-- the assistant's proposed reply, shown first; nothing is used until they say so -->
            <b-tab v-if="aiReply" class="p-2 pt-3">
              <template #title>
                <font-awesome-icon :icon="['fas', 'robot']" class="mr-1"/>AI Reply
              </template>
              <div class="ai-reply border rounded p-2 mb-2">{{ aiReply }}</div>
              <b-button variant="primary" size="sm" class="mr-2" @click="useAiReply">Use this reply</b-button>
              <b-button variant="outline-secondary" size="sm" @click="dismissAiReply">Dismiss</b-button>
            </b-tab>
            <b-tab title="Content" class="p-2 pt-3">
              <message-content-input ref="contentInput" :reference="reference" :message="message" :init="{content, template}"
                                     @content="v => content = v" @typed="typed = true"
                                     @template="v => template = v" :rows="rows"/>
            </b-tab>
            <!-- lazy: the list is rebuilt every time the tab is shown, so a
                 document attached on the Documents tab appears here without
                 the two tabs having to know about each other -->
            <b-tab title="Attachments" @click="activeAttachments" class="p-1 pt-2" lazy
                   :disabled="!(!!sendable || direct.account_address || !!message)">
              <files no-public plain v-if="!!sendable" :url="sendable._links.file_url.href" standalone paste-photos from-store
                     height="277px"/>
            </b-tab>
            <!-- paper the recently-viewed records can produce - an invoice for
                 the order you were just on - rendered on demand and attached as
                 an ordinary file, so from the Attachments tab it is one.

                 The list comes from a message-independent endpoint, so opening
                 this tab saves nothing; the draft is created on the first
                 attach, the way taking the first photo does. -->
            <b-tab title="Documents" class="p-1 pt-2"
                   :disabled="!(!!sendable || direct.account_address || !!message)">
              <div class="overflow-auto p-1" style="height: 277px">
                <documents-history url="/api/message/documents" :attach="attachDocument"/>
              </div>
            </b-tab>
          </b-tabs>
        </b-card>
      </b-col>
      <b-col cols="12" lg="4" v-if="message" class="d-flex flex-column mt-3 mt-lg-0">
        <div>
          Parent Message:
          <b-select v-model="next" :options="pactions" id="next" class="mb-2"/>
          <b-textarea v-model="note" placeholder="Add Parent Notes" rows="4" class="mb-2"/>
        </div>
        <div class="flex-fill">
        </div>
        <div>
          <div class="mb-2" v-if="canCc">
            <div class="d-flex align-items-center">
              <label class="small mb-1 mr-auto">CC</label>
              <!-- prefill everyone who was on the original, minus us and the
                   person being replied to - the server works out that list -->
              <b-button v-if="canReplyAll" variant="link" size="sm" class="p-0 small"
                        v-b-tooltip.hover
                        :title="'CC everyone else on this thread: ' + replyAllAddresses.join(', ')"
                        @click="replyAll">Reply All
              </b-button>
            </div>
            <b-form-tags v-model="cc" size="sm" separator=" ,;" remove-on-delete
                         placeholder="Add a CC address…" :tag-validator="validEmail"
                         invalid-tag-text="Not a valid email address"/>
          </div>

          <!-- route any reply straight back to me instead of the shared queue -->
          <b-form-checkbox v-if="canAssignReplies" v-model="assign_replies_to_me" switch class="small mb-1"
                           v-b-tooltip.hover
                           title="Any reply to this message is assigned to you automatically instead of landing in the unassigned queue">
            Assign replies to me
          </b-form-checkbox>
          <b-button v-if="can('messages.send')" variant="primary" block size="sm" class="mt-2" @click="send(false, true)">Send</b-button>
          <b-button-group class="w-100 mt-2">
            <b-button variant="outline-primary" size="sm" @click="send(true)">Save</b-button>
            <b-button v-if="is_draft" variant="outline-secondary" size="sm"
                      @click="trash(false, () => $emit('done', false))">Delete
            </b-button>
            <b-button variant="outline-secondary" size="sm" @click="cancel">Cancel</b-button>
          </b-button-group>
        </div>
      </b-col>
    </b-row>

    <modal-portal to="footer" v-if="!message">
      <b-button-group class="mr-2">
        <b-button v-if="!!sendable" variant="outline-secondary" size="sm" class="px-3"
                  @click="trash(false, () => $emit('done', true))">Delete
        </b-button>
        <b-button variant="outline-primary" size="sm" class="px-3" @click="send(true, true)">Save</b-button>
      </b-button-group>
      <b-button v-if="can('messages.send')" variant="primary" size="sm" class="px-5" @click="send(false, true)">Send</b-button>
    </modal-portal>
  </div>
</template>

<script>
import {sourceCanCc, sourceCanRouteReplies} from '@/lib/message-source'
import MessageContentInput from "./message-content-input.vue";
import MessageComposeDirect from "./message-compose-direct.vue";
import DocumentsHistory from "../files/documents-history.vue";

export default {
  name: "message-compose",
  components: {DocumentsHistory, MessageComposeDirect, MessageContentInput},

  props: {
    msg: Object,
    reference: Object,
    rows: Number,
    // pre-filled CC, e.g. when the user answered yes to Reply All
    initialCc: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      sendable: this.msg,
      message: null,

      content: null,
      template: false,
      next: null,
      note: null,

      direct: {},
      cc: [],
      dncTo: false,
      assign_replies_to_me: false,
      tab: 0,

      aiGone: false,   // dismissed here
      aiUsed: false,   // copied into Content; marked used once the reply is saved or sent
      typed: false,    // the person has typed in Content
    }
  },

  watch: {
    // switching to a type that cannot CC drops whatever was typed: leaving it
    // in `cc` would post addresses the send path then ignores, which reads to
    // the sender as a CC that was delivered
    canCc(v) {
      if (!v && this.cc.length)
        this.cc = []
    },

    // same reasoning: a flag left set on a type that cannot route replies is
    // stored and never read
    canAssignReplies(v) {
      if (!v && this.assign_replies_to_me)
        this.assign_replies_to_me = false
    },

    'direct.account_address'(v) {
      this.checkRecipientDnc(v)
    }
  },

  created() {
    if (this.sendable) {
      if (this.sendable.state == 'f') {
        if (this.sendable.parent_id)
          this.message = this.sendable.reply_parent
        else
          this.direct.subject = this.sendable.subject

        this.content = this.sendable.content
        this.template = this.sendable.fktemplate
        this.cc = this.initialCc.length
            ? [...this.initialCc]
            : (this.sendable.cc_addresses || []).map(a => a.email)
      } else if (this.sendable.state == 's' || this.sendable.state == 'x') {
        this.alert('Message not draft or able to reply to')
      } else {
        this.message = this.sendable
        this.sendable = null
        this.cc = [...this.initialCc]
      }
    }
  },

  methods: {
    // check the typed recipient as it changes - contact-match answers this
    // without needing the dnc.read permission
    async checkRecipientDnc(email) {
      this.dncTo = false
      if (!email || !this.validEmail(email))
        return

      try {
        const res = await this.asyncAction('/api/message/contact-match', 'GET',
            {noload: true, quiet: true, params: {email}})
        this.dncTo = !!res.do_not_contact
      } catch (e) {
        // advisory only - never block composing because the lookup failed
      }
    },

    validEmail(tag) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(tag).trim())
    },

    replyAll() {
      // union with anything already typed, without duplicating
      const merged = [...this.cc]
      this.replyAllAddresses.forEach(a => {
        if (!merged.some(x => String(x).toLowerCase() === a.toLowerCase()))
          merged.push(a)
      })
      this.cc = merged
    },

    async activeAttachments() {
      if (!this.sendable) {
        const data = this.postData()
        try {
          this.sendable = await this.asyncAction('/api/message', 'POST', {data})
        } catch (e) {
          console.log(e)
          this.tab = this.contentTab
        }
      }
    },

    /**
     * Render a document and attach it, creating the draft if this is the first.
     *
     * The attach endpoint hangs off the message, so there has to be one by the
     * time a document is actually picked - but not before. Reuses
     * activeAttachments so there is one definition of what a fresh draft
     * carries over.
     */
    async attachDocument(doc) {
      await this.activeAttachments()

      if (!this.sendable)
        throw new Error('no draft')

      await this.asyncAction(this.sendable._links.file_url.href + '/documents', 'PUT', {
        data: {model: doc.model, id: doc.id, doc: doc.key}
      })
    },

    async useAiReply() {
      if (this.typed && (this.content || '').trim() && !await this.confirm('Replace what you have typed with the AI reply?'))
        return

      this.$refs.contentInput.setContent(this.aiReply)
      this.typed = false
      this.aiUsed = true
      this.tab = this.contentTab
      await this.attachAiQuote()
    },

    // the assistant's draft quote goes with its reply, once a person has made it ready to send
    async attachAiQuote() {
      const q = this.message && this.message.ai_quote
      if (!q)
        return
      if (q.missing.length) {
        this.toast(`Quote ${q.gli} is not attached: set the ${q.missing.join(' and ')} first`, true, 'Quote not attached')
        return
      }
      await this.attachDocument({model: 'Quote', id: q.id, key: 'quote'})
      this.toast(`Quote ${q.gli} attached`)
    },

    async dismissAiReply() {
      await this.asyncAction('/api/message/' + this.message.id + '?task=ai_dismiss', 'PUT')
      this.aiGone = true
      this.$nextTick(() => this.tab = 0)
      this.toast('Proposed reply dismissed')
    },

    // the proposal went out (as typed or edited), so it stops showing as ready
    async markAiUsed() {
      if (!this.aiUsed || !this.message)
        return

      try {
        await this.asyncAction('/api/message/' + this.message.id + '?task=ai_used', 'PUT', {noload: true, quiet: true})
        this.aiUsed = false
      } catch (e) {
        // advisory only
      }
    },

    async trash(force, done) {
      if (force || await this.confirm('Delete draft?')) {
        await this.asyncAction(this.sendable._links.delete_url.href, this.sendable._links.delete_url.method)
        this.toast('Draft deleted')
        typeof done === 'function' && done()
      }
    },

    postData() {
      return {
        content: this.content,
        fktemplate: this.template && this.template.id,
        parent_id: this.message && this.message.id,
        _parent_note: this.note,
        assign_replies_to_me: this.assign_replies_to_me,
        cc: this.cc,
        ...this.direct
      }
    },

    async send(draft, done) {
      let toast = draft ? 'Saved draft' : 'Sent'

      const data = this.postData()

      data.task = draft ? null : 'send'

      try {
        if (this.sendable && this.sendable.state == 'f') {
          await this.asyncAction(this.sendable._links.self_url.href, 'PUT', {data})
        } else {
          await this.asyncAction('/api/message', 'POST', {data})
        }
      } catch (e) {
        console.log(e)
        return false
      }

      await this.markAiUsed()

      if (this.next) {
        try {
          await this.asyncAction(this.message._links.assign_url.href, this.message._links.assign_url.method, {
            data: {
              assigned_to: this.next,
              _force: true
            }
          })
          const user = this.users.find(u => u.id == this.next)
          toast += ' and re-assigned' + (user ? ' to ' + user.userid : '')
        } catch (e) {
          console.log(e)
          this.toast('Failed to assign message', 'error')
        }
      } else if (draft && !!this.message)
        toast += '. Parent remains ' + this.message.state_description.toLowerCase()

      this.toast(toast)
      this.$emit('done', done)
      return true
    },

    async closing() {
      if (await this.confirm('Save draft?'))
        return await this.send(true)
      else
        return true
    },

    cancel() {
      this.closing().then(r => r && this.$emit('done', false))
    }

  },
  computed: {
    aiReply() {
      const m = this.message
      const ready = m && (m.ai_response_available !== undefined ? m.ai_response_available : m.ai_response_status === 'a')
      return (!this.aiGone && ready && m.ai_response) || null
    },

    contentTab() {
      return this.aiReply ? 1 : 0
    },

    /**
     * How this message will actually go out.
     *
     * Three places to look, because created() moves the source around: a
     * draft keeps it on `sendable`; replying to a non-draft assigns the
     * message to `message` and sets `sendable` to NULL, which is the case
     * that matters most here; and a new message has only whatever was picked
     * in the Send Via list.
     */
    sendSource() {
      return (this.sendable && this.sendable.source)
          || (this.message && this.message.source)
          || (this.direct && this.direct.source)
          || null
    },

    /**
     * Only email carries a CC.
     *
     * The field was offered on every type regardless, so addresses typed on
     * an eBay or internal message were accepted, stored, and then silently
     * dropped at send.
     */
    canCc() {
      return sourceCanCc(this.sendSource)
    },

    /**
     * Only an emailed message can have its replies routed back.
     *
     * Same silent-no-op the CC field had: the switch was offered on every
     * type, and two eBay messages in production already carry a
     * reply_assign_to that nothing will ever read.
     */
    canAssignReplies() {
      return sourceCanRouteReplies(this.sendSource)
    },

    users() {
      return this.$store.state.settings.defaults.users
    },

    // everyone else who was on the original message - the server works out
    // "minus us, minus the person being replied to" so both frontends agree
    replyAllAddresses() {
      return ((this.message && this.message.reply_all_cc) || []).map(a => a.email)
    },

    canReplyAll() {
      return !!this.message && this.replyAllAddresses.length > 0
    },

    /**
     * Warn before writing to a suppressed address. The recipient comes from the
     * message being replied to (or a live lookup while composing); CCs are
     * checked too because the mailer drops suppressed ones silently, which would
     * otherwise look like the mail simply never arrived.
     */
    dncWarning() {
      const parts = []
      const msgDnc = (this.sendable && this.sendable.dnc) || (this.message && this.message.dnc)

      if (this.dncTo || (msgDnc && msgDnc.to))
        parts.push('The recipient is on the do-not-contact list.')

      const badCc = (msgDnc && msgDnc.cc) || []
      if (badCc.length)
        parts.push(`These CC addresses are suppressed and will not be sent to: ${badCc.join(', ')}.`)

      return parts.length ? parts.join(' ') : null
    },

    is_draft() {
      return this.sendable && this.sendable.state == 'f'
    },

    pactions() {
      return [{
        text: '--- Done Message ---',
        value: null
      }].concat(this.users.map((s) => {
        return {text: 'Assign: ' + s.name, value: s.id}
      }))
    }
  }
}
</script>

<style scoped>
.ai-reply {
  white-space: pre-wrap;
  background: #f3f8fb;
  max-height: 260px;
  overflow-y: auto;
}
</style>